import { useEffect } from "react";
import { Box, useTheme } from "@mui/material";
import { Outlet } from "react-router-dom";
import { ACCENT } from "./accent";
import PublicHeader from "../../PublicHeader";
import { APP_BASE } from "./paths";

const DEFAULT_TITLE = "Analyze your flight log — Vecros";
const DEFAULT_DESC =
  "Upload an ArduPilot flight log for instant summaries, charts and AI insight — or explore flight logs the community has shared. Free, no sign-up to browse.";
const DEFAULT_APP = APP_BASE;

// Per-route SEO without a dependency: set title + description + OG/Twitter tags
// on mount and restore them on unmount, so only the public pages carry them.
const applyPublicSeo = (title, desc) => {
  const prevTitle = document.title;
  document.title = title;

  const restores = [];
  const setMeta = (attr, key, content) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (el) {
      const prev = el.getAttribute("content");
      restores.push(() => el.setAttribute("content", prev ?? ""));
    } else {
      el = document.createElement("meta");
      el.setAttribute(attr, key);
      document.head.appendChild(el);
      restores.push(() => el.remove());
    }
    el.setAttribute("content", content);
  };

  setMeta("name", "description", desc);
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:type", "website");
  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", desc);

  return () => {
    document.title = prevTitle;
    restores.forEach((fn) => fn());
  };
};

/**
 * Chrome for the public tool pages — the site header (same one the landing
 * wears) over the tool, and NO app navbar/sidebar/assistant: those are for
 * signed-in users, in Body.
 */
const PublicShell = ({
  accent = ACCENT,
  appPath = DEFAULT_APP,
  seoTitle = DEFAULT_TITLE,
  seoDesc = DEFAULT_DESC,
} = {}) => {
  const theme = useTheme();

  useEffect(() => applyPublicSeo(seoTitle, seoDesc), [seoTitle, seoDesc]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.main,
      }}
    >
      <PublicHeader accent={accent} appPath={appPath} />

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <Outlet />
      </Box>
    </Box>
  );
};

export default PublicShell;
